import { reaction } from 'mobx'
// Type-only: firebase-repository must not bundle livekit-client. Event names are
// plain strings and enum comparisons use casts, matching the rest of this
// package (see MeetingLivekitController / DeadAirDetector).
import type {
  ConnectionState,
  LocalTrackPublication,
  Participant,
  RemoteParticipant,
  Room,
  Track,
} from 'livekit-client'
import type { MeetingCubit } from '../../MeetingCubit'
import type { SoundcheckController } from './SoundcheckController'
import { ServerIdentities } from '../../../util/identities'
import { SlideType } from '../../../firestore/Slide/types'
import { secretaryStatusSchema } from './types'
import type { MicContext } from './types'

// livekit enum string values, inlined so we don't import the runtime package.
const MICROPHONE_SOURCE = 'microphone' as Track.Source
const CONNECTED_STATE = 'connected' as ConnectionState

/**
 * Owns every Soundcheck room subscription and the context mapping, and returns
 * one disposer that removes what it added, including per-track listeners. It
 * only considers publications whose source is Microphone; screen-share audio is
 * ignored. It processes an already-published microphone when attaching, and then
 * publish, unpublish, ended and restarted.
 */
export function attachSoundcheckToRoom(
  room: Room,
  meeting: MeetingCubit,
  controller: SoundcheckController
): () => void {
  const disposers: (() => void)[] = []
  const mic = controller.mic

  controller.setLocalIdentity(meeting.repository.uid ?? null)
  controller.reportTranscriptionExpected(!meeting.isPreview)

  // Forward the meeting MicCheck's speech to the controller. These also feed the
  // existing participation callbacks through their current owner; Soundcheck
  // never stops them.
  mic.onSpeechStart = () => controller.reportLocalSpeechStart()
  mic.onSpeechEnd = () => controller.reportLocalSpeechEnd()

  // Feed the controller every MicCheck report; this is also where the meeting's
  // soundcheck_mic_result telemetry fires.
  disposers.push(
    reaction(
      () => mic.report,
      (report) => controller.reportMicReport(report),
      { fireImmediately: true }
    )
  )

  // Context is driven by whether a microphone is actually live, not by the
  // participant's role: an internal/hidden user who unmutes and publishes a real
  // mic must still be checked. `getTrackPublication` is not observable, so the
  // reaction below covers the observable inputs and observeTrack/unpublish call
  // refreshContext directly when a publication appears or goes away.
  const refreshContext = () => mic.setContext(computeContext(meeting, room))

  const isMicrophone = (pub: LocalTrackPublication) =>
    pub.source === MICROPHONE_SOURCE

  const trackListeners = new Map<string, () => void>()

  const observeTrack = (pub: LocalTrackPublication) => {
    if (!isMicrophone(pub)) return
    const track = pub.track
    if (!track) return
    // Only report a real SID. A publish handshake can fire before the SID is
    // assigned; reporting null there would clobber a SID set moments earlier and
    // needlessly reset the delivery/heartbeat evidence.
    if (pub.trackSid) controller.reportTrackSid(pub.trackSid)
    mic.setTrack(track.mediaStreamTrack ?? null)
    // A live publication just appeared; re-decide active vs not_required.
    refreshContext()

    // A new underlying MediaStreamTrack is a new observation even when the
    // publication SID stays the same, so re-attach on restart. Keyed by the
    // publication SID so it can be removed on unpublish; without a SID there is
    // no key we could ever cancel, so skip the listeners rather than leak them.
    const key = pub.trackSid
    if (!key) return
    // Replace any listeners from a previous observation of the same SID.
    trackListeners.get(key)?.()
    // Reset before setTrack: setTrack ends the open speech interval, and that
    // end must not evaluate against the old capture's server confirmations.
    const onRestarted = () => {
      controller.reportTrackRestarted()
      mic.setTrack(track.mediaStreamTrack ?? null)
    }
    const onEnded = () => mic.setTrack(track.mediaStreamTrack ?? null)
    track.on('restarted', onRestarted)
    track.on('ended', onEnded)
    trackListeners.set(key, () => {
      track.off('restarted', onRestarted)
      track.off('ended', onEnded)
    })
  }

  const onLocalTrackPublished = (pub: LocalTrackPublication) =>
    observeTrack(pub)
  const onLocalTrackUnpublished = (pub: LocalTrackPublication) => {
    if (!isMicrophone(pub)) return
    // Remove this track's restarted/ended listeners so they don't leak across a
    // republish (device swap, reconnect).
    const key = pub.trackSid
    if (key) {
      trackListeners.get(key)?.()
      trackListeners.delete(key)
    }
    controller.reportTrackSid(null)
    mic.setTrack(null)
    // The mic went away; an observer with no publication becomes not_required.
    refreshContext()
  }
  room.on('localTrackPublished', onLocalTrackPublished)
  room.on('localTrackUnpublished', onLocalTrackUnpublished)
  disposers.push(() => {
    room.off('localTrackPublished', onLocalTrackPublished)
    room.off('localTrackUnpublished', onLocalTrackUnpublished)
    for (const off of trackListeners.values()) off()
    trackListeners.clear()
  })

  // Process an already-published microphone.
  for (const pub of room.localParticipant.audioTrackPublications.values()) {
    observeTrack(pub as LocalTrackPublication)
  }

  // Server-confirmed speech.
  const onActiveSpeakers = (speakers: Participant[]) => {
    const me = meeting.repository.uid
    // Without our own identity we cannot tell if the server heard us; skip rather
    // than compare against undefined (which is always false and would manufacture
    // a delivery false positive).
    if (!me) return
    const speaking = speakers.some((s) => s.identity === me)
    controller.reportServerSpeaking(speaking)
  }
  room.on('activeSpeakersChanged', onActiveSpeakers)
  disposers.push(() => room.off('activeSpeakersChanged', onActiveSpeakers))

  // Secretary heartbeat, type 16, from the Secretary participant.
  const onData = (payload: Uint8Array, participant?: RemoteParticipant) => {
    if (participant?.identity !== ServerIdentities.Secretary) return
    let parsed: unknown
    try {
      parsed = JSON.parse(new TextDecoder().decode(payload))
    } catch {
      return
    }
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      (parsed as { type?: unknown }).type !== 16
    ) {
      return
    }
    // QA tooling can drop heartbeats to exercise the no_heartbeat observation
    // against a live worker.
    if (meeting.qa_soundcheckDropHeartbeats) return
    const result = secretaryStatusSchema.safeParse(parsed)
    if (result.success) controller.reportHeartbeat(result.data)
  }
  room.on('dataReceived', onData)
  disposers.push(() => room.off('dataReceived', onData))

  // Secretary presence.
  const onPresent = () => controller.reportSecretaryPresence(true)
  const onAbsent = () => controller.reportSecretaryPresence(false)
  meeting.secretaryController.events.on('connected', onPresent)
  meeting.secretaryController.events.on('reconnected', onPresent)
  meeting.secretaryController.events.on('disconnected', onAbsent)
  meeting.secretaryController.events.on('timeout', onAbsent)
  disposers.push(() => {
    meeting.secretaryController.events.off('connected', onPresent)
    meeting.secretaryController.events.off('reconnected', onPresent)
    meeting.secretaryController.events.off('disconnected', onAbsent)
    meeting.secretaryController.events.off('timeout', onAbsent)
  })

  // Re-decide the context whenever an observable input changes. Only genuinely
  // observable inputs belong here — clientReconnectInFlight and micMutedByUser
  // are plain fields on MeetingCubit, so a reaction can't track them; connection
  // state and mute are re-driven from room events below, and track presence
  // (getTrackPublication) from publish/unpublish.
  disposers.push(
    reaction(
      () => [
        meeting.avCheckPassed,
        meeting.isDemo,
        meeting.currentUserIsHidden,
        !meeting.currentSlide,
        meeting.endOfSessionStudentsAreIsolated,
      ],
      () => refreshContext(),
      { fireImmediately: true }
    )
  )

  // Connection state and mute changes are not observable fields, so drive the
  // context off the room's events. Without this a reconnect leaves the context
  // stuck on 'reconnecting' and the mic is never checked again.
  const onConnectionOrMute = () => refreshContext()
  room.on('connectionStateChanged', onConnectionOrMute)
  room.on('trackMuted', onConnectionOrMute)
  room.on('trackUnmuted', onConnectionOrMute)
  disposers.push(() => {
    room.off('connectionStateChanged', onConnectionOrMute)
    room.off('trackMuted', onConnectionOrMute)
    room.off('trackUnmuted', onConnectionOrMute)
  })

  // Solo discussion for the transcript safeguard.
  disposers.push(
    reaction(
      () =>
        meeting.isInSoloMode &&
        meeting.currentSlide?.type === SlideType.discussion,
      (active) => controller.reportSoloDiscussion(active),
      { fireImmediately: true }
    )
  )

  return () => {
    mic.onSpeechStart = undefined
    mic.onSpeechEnd = undefined
    for (const dispose of disposers) dispose()
  }
}

/**
 * Why the microphone is on or off. Keyed off what is actually happening, not the
 * participant's role:
 *
 * - reconnecting while the client is reconnecting;
 * - not_required on isolating slides (the mic is muted for isolation) and for a
 *   genuine observer who has no microphone publication at all;
 * - user_muted when the student muted deliberately;
 * - otherwise active — including an internal/hidden user who published a mic and
 *   is speaking, which must still be checked.
 */
function computeContext(meeting: MeetingCubit, room: Room): MicContext {
  // Until the student has joined the meeting (still in the AV check / lobby on
  // the shared room), no meeting microphone is expected — do not check. Demo
  // rooms skip the AV check, so they are treated as joined.
  if (!meeting.avCheckPassed && !meeting.isDemo) return 'not_required'
  // The room's own connection state is the reliable reconnect signal;
  // clientReconnectInFlight is a non-observable field and cannot be tracked.
  if (room.state !== CONNECTED_STATE) return 'reconnecting'
  // Isolation mutes the mic on purpose; do not treat that as a fault.
  const isIsolated =
    !meeting.currentSlide || meeting.endOfSessionStudentsAreIsolated
  if (isIsolated) return 'not_required'
  if (meeting.micMutedByUser) return 'user_muted'
  const hasLiveMic =
    !!room.localParticipant.getTrackPublication(MICROPHONE_SOURCE)?.track
  // An observer with no mic publication has nothing to check; a participant who
  // is expected to have a mic but has none falls through to active so a genuine
  // no_track surfaces.
  if (!hasLiveMic && meeting.currentUserIsHidden) return 'not_required'
  return 'active'
}
